import { onUnmounted, watch, type Ref } from 'vue'
import PhotoSwipeLightbox from 'photoswipe/lightbox'
import 'photoswipe/style.css'

interface GalleryImage {
  id: string
  src: string
  width: number
  height: number
  label?: string
  thumbEl?: HTMLElement
  thumbSrc?: string
}

interface GalleryState {
  images: Map<string, GalleryImage>
  lightbox: PhotoSwipeLightbox | null
  order: string[]
}

// Global registry of galleries
const galleries = new Map<string, GalleryState>()

/**
 * Get image dimensions from image source
 */
async function getImageDimensions(src: string): Promise<{ width: number; height: number }> {
  return new Promise((resolve) => {
    const img = new Image()
    img.onload = () => {
      resolve({
        width: img.naturalWidth || 1920,
        height: img.naturalHeight || 1080,
      })
    }
    img.onerror = () => {
      resolve({ width: 1920, height: 1080 })
    }
    img.src = src
  })
}

/**
 * Get or create a gallery state
 */
function getOrCreateGallery(galleryId: string): GalleryState {
  if (!galleries.has(galleryId)) {
    galleries.set(galleryId, {
      images: new Map(),
      lightbox: null,
      order: [],
    })
  }
  return galleries.get(galleryId)!
}

/**
 * Get the best available thumbnail URL for the lightbox thumbnail strip.
 */
function getThumbStripSrc(img: GalleryImage): string {
  if (img.thumbSrc) return img.thumbSrc
  if (img.thumbEl && img.thumbEl instanceof HTMLImageElement) return img.thumbEl.src
  return img.src
}


/**
 * Rebuild the lightbox for a gallery
 */
function rebuildLightbox(galleryId: string) {
  const gallery = galleries.get(galleryId)
  if (!gallery) return

  // Destroy existing lightbox
  if (gallery.lightbox) {
    gallery.lightbox.destroy()
    gallery.lightbox = null
  }

  // Don't create lightbox if no images
  if (gallery.images.size === 0) return

  // Build ordered list of image IDs (used by dataSource and thumbnail strip)
  const orderedIds = gallery.order.filter((id) => gallery.images.has(id))

  // Build data source from registered images (include msrc for thumbnail strip)
  const dataSource = orderedIds.map((id) => {
    const img = gallery.images.get(id)!
    return {
      id: img.id,
      src: img.src,
      msrc: getThumbStripSrc(img),
      width: img.width || 1920,
      height: img.height || 1080,
      label: img.label || '',
    }
  })

  // Create new lightbox with dataSource
  const lightbox = new PhotoSwipeLightbox({
    dataSource,
    showHideAnimationType: 'zoom',
    closeOnVerticalDrag: true,
    pinchToClose: true,
    wheelToZoom: true,
    showAnimationDuration: 300,
    hideAnimationDuration: 250,
    loop: false,
    // @ts-ignore - PhotoSwipe dynamic import
    pswpModule: () => import('photoswipe'),
  })

  // Add thumb element filter for zoom animation
  // @ts-ignore - PhotoSwipe types are not perfect
  lightbox.addFilter('thumbEl', (_thumbEl: HTMLElement | null | undefined, data: any): HTMLElement | null => {
    const imageId = data.id
    const img = gallery.images.get(imageId)
    return (img?.thumbEl || _thumbEl || null) as HTMLElement | null
  })

  // Add placeholder filter for smoother loading
  // @ts-ignore - PhotoSwipe filter typing mismatch
  lightbox.addFilter('placeholderSrc', (_placeholderSrc: string | false, slide: any) => {
    const imageId = (slide.data as any).id
    const img = gallery.images.get(imageId)
    if (img?.thumbEl && img.thumbEl instanceof HTMLImageElement) {
      return img.thumbEl.src
    }
    return _placeholderSrc
  })

  const showThumbs = orderedIds.length > 1

  // Register caption + thumbnail strip UI elements
  lightbox.on('uiRegister', () => {
    if (!lightbox.pswp?.ui) return

    // Caption element (positioned above thumbnail strip when present)
    lightbox.pswp.ui.registerElement({
      name: 'caption',
      order: 9,
      isButton: false,
      appendTo: 'root',
      html: '',
      onInit: (el: HTMLElement, pswp: any) => {
        el.style.cssText = `
          position: absolute;
          bottom: ${showThumbs ? '72px' : '0'};
          left: 0;
          right: 0;
          padding: 12px 24px;
          text-align: center;
          color: white;
          font-size: 1rem;
          font-weight: 500;
          background: linear-gradient(transparent, rgba(0,0,0,0.5));
          pointer-events: none;
        `
        const updateCaption = () => {
          const currentId = orderedIds[pswp.currIndex]
          const img = currentId ? gallery.images.get(currentId) : null
          el.textContent = img?.label || ''
        }
        pswp.on('change', updateCaption)
        updateCaption()
      },
    } as any)

    // Thumbnail strip (only when 2+ images)
    if (showThumbs) {
      lightbox.pswp.ui.registerElement({
        name: 'thumbStrip',
        className: 'pswp__thumb-strip',
        order: 10,
        isButton: false,
        appendTo: 'root',
        html: '',
        onInit: (el: HTMLElement, pswp: any) => {
          el.style.cssText = `
            position: absolute;
            bottom: 0;
            left: 0;
            right: 0;
            display: flex;
            justify-content: center;
            gap: 6px;
            padding: 10px 16px;
            background: rgba(0, 0, 0, 0.6);
            overflow-x: auto;
            z-index: 10;
          `

          const thumbEls: HTMLImageElement[] = []
          let prevIndex = -1

          // Build thumbnail images from dataSource
          for (let i = 0; i < pswp.getNumItems(); i++) {
            const slideData = dataSource[i]
            if (!slideData) continue

            const thumb = document.createElement('img')
            thumb.src = slideData.msrc || slideData.src
            thumb.alt = slideData.label || ''
            thumb.style.cssText = `
              width: 48px;
              height: 48px;
              object-fit: cover;
              border-radius: 4px;
              cursor: pointer;
              opacity: 0.5;
              transition: opacity 0.2s, outline-color 0.2s;
              outline: 2px solid transparent;
              outline-offset: 1px;
              flex-shrink: 0;
            `
            thumb.onerror = () => {
              if (thumb.src !== slideData.src) {
                thumb.src = slideData.src
              }
            }
            thumb.onclick = () => {
              pswp.goTo(i)
            }
            el.appendChild(thumb)
            thumbEls.push(thumb)
          }

          // Update active thumbnail on slide change
          const updateActive = () => {
            if (prevIndex >= 0 && thumbEls[prevIndex]) {
              thumbEls[prevIndex].style.opacity = '0.5'
              thumbEls[prevIndex].style.outlineColor = 'transparent'
            }
            const curr = thumbEls[pswp.currIndex]
            if (curr) {
              curr.style.opacity = '1'
              curr.style.outlineColor = 'rgba(255, 255, 255, 0.9)'
              curr.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' })
            }
            prevIndex = pswp.currIndex
          }

          pswp.on('change', updateActive)
          // Highlight initial thumb
          updateActive()
        },
      } as any)
    }
  })

  lightbox.init()
  gallery.lightbox = lightbox
}

// ── Imperative API (no lifecycle hooks — safe to call anywhere) ──────

interface RegisterImageOptions {
  galleryId: string
  imageId: string
  src: string
  label?: string
  thumbEl?: HTMLElement
  thumbSrc?: string
}

/**
 * Register or update a single image in a gallery.
 * Can be called from watchEffect, event handlers, etc.
 */
export async function registerGalleryImage(options: RegisterImageOptions) {
  const { galleryId, imageId, src, label, thumbEl, thumbSrc } = options
  const gallery = getOrCreateGallery(galleryId)

  if (!src) {
    gallery.images.delete(imageId)
    const orderIdx = gallery.order.indexOf(imageId)
    if (orderIdx > -1) gallery.order.splice(orderIdx, 1)
    rebuildLightbox(galleryId)
  } else {
    // Reserve the order slot synchronously so that concurrent calls
    // (e.g. registering foto 1 and foto 2 in a loop) maintain the
    // caller's insertion order regardless of which image loads first.
    if (!gallery.order.includes(imageId)) {
      gallery.order.push(imageId)
    }

    const dimensions = await getImageDimensions(src)
    gallery.images.set(imageId, {
      id: imageId,
      src,
      width: dimensions.width,
      height: dimensions.height,
      label,
      thumbEl,
      thumbSrc,
    })

    rebuildLightbox(galleryId)
  }
}

/**
 * Remove an image from a gallery. Safe to call anywhere.
 */
export function unregisterGalleryImage(galleryId: string, imageId: string) {
  const gallery = galleries.get(galleryId)
  if (!gallery) return

  gallery.images.delete(imageId)
  const orderIdx = gallery.order.indexOf(imageId)
  if (orderIdx > -1) gallery.order.splice(orderIdx, 1)

  if (gallery.images.size === 0) {
    if (gallery.lightbox) gallery.lightbox.destroy()
    galleries.delete(galleryId)
  } else {
    rebuildLightbox(galleryId)
  }
}

/**
 * Update the thumbnail element for an image (e.g. after DOM ref changes).
 */
export function updateGalleryThumbEl(galleryId: string, imageId: string, thumbEl: HTMLElement) {
  const gallery = galleries.get(galleryId)
  if (!gallery) return
  const img = gallery.images.get(imageId)
  if (img) img.thumbEl = thumbEl
}

/**
 * Open a gallery lightbox at the specified image.
 */
export function openGalleryAt(galleryId: string, imageId: string) {
  const gallery = galleries.get(galleryId)
  if (!gallery?.lightbox) return

  const orderedIds = gallery.order.filter((id) => gallery.images.has(id))
  const index = orderedIds.indexOf(imageId)
  if (index >= 0) gallery.lightbox.loadAndOpen(index)
}

/**
 * Remove all images whose IDs match a prefix and rebuild the gallery.
 * Useful for bulk cleanup (e.g. removing all "original-*" images).
 */
export function unregisterGalleryImagesByPrefix(galleryId: string, prefix: string) {
  const gallery = galleries.get(galleryId)
  if (!gallery) return

  const toRemove = [...gallery.images.keys()].filter((id) => id.startsWith(prefix))
  for (const id of toRemove) {
    gallery.images.delete(id)
    const orderIdx = gallery.order.indexOf(id)
    if (orderIdx > -1) gallery.order.splice(orderIdx, 1)
  }

  if (gallery.images.size === 0) {
    if (gallery.lightbox) gallery.lightbox.destroy()
    galleries.delete(galleryId)
  } else {
    rebuildLightbox(galleryId)
  }
}

// ── Composable API (uses lifecycle hooks — must be called during setup) ──

interface UsePhotoSwipeGalleryOptions {
  galleryId: string
  imageId: string
  imageSource: Ref<string>
  thumbnailRef: Ref<HTMLImageElement | null>
  label?: string
  thumbSrc?: Ref<string>
}

/**
 * Composable for registering a single image with a shared PhotoSwipe gallery.
 * Uses watch() and onUnmounted() — MUST be called during component setup().
 *
 * For dynamic lists of images (e.g. v-for), use the imperative API
 * (registerGalleryImage / unregisterGalleryImage) with watchEffect instead.
 */
export function usePhotoSwipeGallery(options: UsePhotoSwipeGalleryOptions) {
  const { galleryId, imageId, imageSource, thumbnailRef, label, thumbSrc } = options

  const openGallery = () => openGalleryAt(galleryId, imageId)

  // Watch for image source changes
  watch(
    () => imageSource.value,
    (src) => {
      registerGalleryImage({
        galleryId,
        imageId,
        src,
        label,
        thumbEl: thumbnailRef.value || undefined,
        thumbSrc: thumbSrc?.value,
      })
    },
    { immediate: true },
  )

  // Watch for thumbnail ref changes
  watch(
    () => thumbnailRef.value,
    (el) => {
      if (el) updateGalleryThumbEl(galleryId, imageId, el)
    },
  )

  // Cleanup on unmount
  onUnmounted(() => {
    unregisterGalleryImage(galleryId, imageId)
  })

  return {
    openGallery,
  }
}
